/**
 * 后台「诉讼台账 · 四表模板」管理组件。
 *
 * 设计要点：
 * - 不再提供 Excel 单元格编辑器（改表头请在本地 Excel 改好再导入）。
 * - 三段式：① 当前模板 ② 四表识别情况 ③ 字段与列 ④ 客户端预览。
 * - 所有改动先留在本地，点「保存并发布」才写回平台配置。
 */
import {
  LITIGATION_COURT_NUMBER_LAYERS,
  LITIGATION_FIELD_RULE_KINDS,
  LITIGATION_NUMBERING_KIND_LABELS,
  LITIGATION_PLAN_STANDARD_FIELDS,
  litigationFieldFillGuide,
  litigationPlanFieldLabel,
  litigationPlanMappingSourceKind,
  resolveLitigationFieldRule,
  suggestLitigationPlanField,
  type LitigationFieldRuleOverrides,
  type LitigationPlanFieldMapping,
  type LitigationPlanStandardField,
} from '../common/litigationLedgerPlan.js';
import {LITIGATION_DEFAULT_SHEETS, litigationFieldsForSheetKind, type LitigationSheetKind} from '../common/litigationLedgerSheets.js';
import {api} from './api.js';
import {currentAdminEnvironment, runAgainstTestAdmin} from './admin-environment.js';
import {apiUrl} from './config.js';
import {escapeHtml} from './layout.js';

const CONFIG_PATH = '/api/admin/resources/litigation-template';

type SheetMap = {
  sheetKind: LitigationSheetKind;
  /** 真正识别到的表类型；null = 未识别（仍保留并可在后台配置）。 */
  detectedKind?: LitigationSheetKind | null;
  sheetName: string;
  headerRow: number;
  dataStartRow: number;
  exportFields: LitigationPlanFieldMapping[];
};

type PlatformConfig = {
  version: number;
  updatedAt: string;
  fileName: string;
  sheetName: string;
  headerRow: number;
  dataStartRow: number;
  exportFields: LitigationPlanFieldMapping[];
  sheetMaps: SheetMap[];
  sheetNames?: string[];
  /** 管理员配置的字段填写规则覆盖。 */
  fieldRuleOverrides?: LitigationFieldRuleOverrides;
  listFieldIds: string[];
};

type SheetHealth = {
  sheetKind: LitigationSheetKind;
  configSheetName: string;
  sheetExists: boolean;
  detectedKind: LitigationSheetKind | null;
  headerRow: number;
  dataStartRow: number;
  columnCount: number;
  mappedColumnCount: number;
  unmappedColumnCount: number;
  hasIssue: boolean;
  issues: string[];
};

type PlatformHealth = {
  ok: boolean;
  version: number;
  updatedAt: string;
  fileName: string;
  hasCustomXlsx: boolean;
  sizeBytes: number;
  workbookSheetNames: string[];
  sheets: SheetHealth[];
  error?: string;
  searchedPaths?: string[];
};

type EditorState = {
  config: PlatformConfig;
  health: PlatformHealth;
  /** 当前编辑的工作表名（上传的 Excel 里的真实表名）。 */
  activeSheetName: string;
  /** 每张表当前的字段行，键为工作表名。数组顺序即台账列序。 */
  orderBySheet: Record<string, LitigationPlanFieldMapping[]>;
  headerRow: number;
  dataStartRow: number;
  dirty: boolean;
  /** 当前展开填写规则编辑面板的字段 ID。 */
  expandedRuleField: string;
  query: string;
  filter: 'all' | 'enabled' | 'disabled' | 'unmapped';
};

const SHEET_LABEL: Record<LitigationSheetKind, string> = {
  cases: '案件总表',
  parties: '案件背景',
  procedure: '程序与期限',
  progress: '进展记录',
};

const SOURCE_KIND_LABEL: Record<ReturnType<typeof litigationPlanMappingSourceKind>, string> = {
  litigation: '系统写入',
  choice: '固定选项',
  manual: '人工填写',
  computed: '系统生成',
  custom: '系统取号',
  reference: '跨表引用',
};

/** 与客户端一致的字段分组标题（用于折叠分组）。 */
const GROUP_LABEL: Record<string, string> = {
  basic: '基本信息',
  party: '当事人',
  procedure: '程序与期限',
  result: '结果与结案',
  meta: '元信息',
  custom: '自定义',
};

function columnLetter(index: number): string {
  let value = index;
  let letters = '';
  while (value >= 0) {
    letters = String.fromCharCode((value % 26) + 65) + letters;
    value = Math.floor(value / 26) - 1;
  }
  return letters;
}

/** 该表在标准目录里的字段（决定有哪些行、默认顺序）。 */
function catalogFieldsForKind(kind: LitigationSheetKind): string[] {
  const spec = LITIGATION_DEFAULT_SHEETS.find((item) => item.kind === kind);
  return spec ? [...spec.fieldIds] : [];
}

/** 由配置与标准目录合成"该表的完整字段行"。
 *
 * 分两段：
 *  1) 用户 Excel 里真实存在的列（保留真实列位与表头，未映射的也保留，用于提示"未映射"）
 *  2) （仅当该表被识别为四表之一时）标准字段目录里还没出现的字段，供映射使用
 *
 * @param map 该工作表的配置映射
 */
function buildOrderForKind(map: SheetMap): LitigationPlanFieldMapping[] {
  const existing = (map.exportFields ?? []).slice();
  const usedColumns = new Set<number>();
  const usedFields = new Set<string>();
  const rows: LitigationPlanFieldMapping[] = [];
  for (const field of existing) {
    usedColumns.add(field.columnIndex);
    if (field.sourceField) usedFields.add(field.sourceField);
    rows.push({...field});
  }
  // 未识别为四表的工作表不补标准字段目录——它只按自身结构配置
  const kind = map.detectedKind ?? null;
  if (!kind) return rows;
  // 补齐标准目录里还没出现的字段：列位顺延到已有列之后
  let nextColumn = rows.reduce((max, item) => Math.max(max, item.columnIndex), -1) + 1;
  for (const fieldId of catalogFieldsForKind(kind)) {
    if (usedFields.has(fieldId)) continue;
    usedFields.add(fieldId);
    while (usedColumns.has(nextColumn)) nextColumn += 1;
    usedColumns.add(nextColumn);
    rows.push({
      enabled: false,
      columnIndex: nextColumn,
      columnLetter: columnLetter(nextColumn),
      header: '',
      sourceField: fieldId,
      valueMode: 'source',
    });
    nextColumn += 1;
  }
  return rows;
}

/**
 * 按新的表类型重新映射该表的字段：
 * 把每一列的真实表头重新跑一次字段推荐，命中则映射、未命中保持未映射（由管理员手工指定）；
 * 若某列原本已映射且新类型下该字段名不再被推荐，则保留原映射（不擅自清空管理员的成果）。
 *
 * @param rows 当前字段行
 * @param nextKind 新的表类型；null = 不参与台账
 */
function remapRowsForKind(
  rows: LitigationPlanFieldMapping[],
  nextKind: LitigationSheetKind | null,
): LitigationPlanFieldMapping[] {
  const mappedHeaders = new Set(rows.filter((item) => item.sourceField).map((item) => item.sourceField));
  const next = rows.map((row) => {
    if (!nextKind) {
      // 不参与台账：清空映射但保留表头与列位
      return {...row, sourceField: '', enabled: false};
    }
    const header = String(row.header ?? '').trim();
    if (!header) return {...row, sourceField: '', enabled: false};
    const suggested = suggestLitigationPlanField(header, nextKind);
    if (suggested) {
      // 同一字段不重复映射到多列
      if (mappedHeaders.has(suggested) && row.sourceField !== suggested) {
        return {...row, sourceField: '', enabled: false};
      }
      return {...row, sourceField: suggested, enabled: true};
    }
    // 推荐不出来：保留原有映射（若有），否则保持未映射
    return row.sourceField ? row : {...row, enabled: false};
  });
  return next;
}

/** 该行当前生效的填写规则标签（后台覆盖优先）。 */
function ruleBadgeLabel(fieldId: string, overrides?: LitigationFieldRuleOverrides | null): {label: string; customized: boolean} {
  const resolved = resolveLitigationFieldRule(fieldId, overrides);
  const info = LITIGATION_FIELD_RULE_KINDS.find((item) => item.id === resolved.ruleKind);
  return {label: info?.label ?? '人工填写', customized: resolved.customized};
}

/** 某字段的规则编辑面板（展开后显示）。 */
function renderRuleEditor(fieldId: string, overrides?: LitigationFieldRuleOverrides | null): string {
  const resolved = resolveLitigationFieldRule(fieldId, overrides);
  const options = LITIGATION_FIELD_RULE_KINDS.map((item) => `<option value="${item.id}" ${resolved.ruleKind === item.id ? 'selected' : ''}>${escapeHtml(item.label)}</option>`).join('');
  const hint = LITIGATION_FIELD_RULE_KINDS.find((item) => item.id === resolved.ruleKind)?.hint ?? '';
  const examples = overrides?.[fieldId]?.courtNumberExamples ?? {};
  const courtLayers = fieldId === 'courtNo'
    ? `<div class="lt2-court-layers">${LITIGATION_COURT_NUMBER_LAYERS.map((layer) => `<label class="lt2-rule-field">${escapeHtml(layer.label)}
        <input type="text" data-rule-court-field="${escapeHtml(fieldId)}" data-rule-court-layer="${layer.id}" value="${escapeHtml(examples[layer.id] ?? layer.example)}" placeholder="${escapeHtml(layer.example)}">
      </label>`).join('')}</div>`
    : `<div class="lt2-rule-row">
      <label class="lt2-rule-field">可选值（用 / 分隔）
        <input type="text" data-rule-allowed="${escapeHtml(fieldId)}" value="${escapeHtml(resolved.allowed ?? '')}" placeholder="例如：是 / 否">
      </label>
      <label class="lt2-rule-field">示例
        <input type="text" data-rule-example="${escapeHtml(fieldId)}" value="${escapeHtml(resolved.example ?? '')}" placeholder="例如：2026-01-08">
      </label>
    </div>`;
  return `<div class="lt2-rule-editor" data-rule-editor="${escapeHtml(fieldId)}">
    <div class="lt2-rule-row">
      <label class="lt2-inline-field">规则种类
        <select class="lt2-filter" data-rule-kind="${escapeHtml(fieldId)}">${options}</select>
      </label>
      <span class="lt2-rule-kind-hint">${escapeHtml(hint)}</span>
      <button type="button" class="lt2-ghost-btn" data-rule-reset="${escapeHtml(fieldId)}" ${resolved.customized ? '' : 'disabled'}>恢复系统默认</button>
    </div>
    <label class="lt2-rule-field">规则说明
      <textarea data-rule-text="${escapeHtml(fieldId)}" rows="2" placeholder="写清这个字段该怎么填">${escapeHtml(resolved.rule)}</textarea>
    </label>
    ${courtLayers}
  </div>`;
}

/** 该行是否还没有映射到台账字段。 */
function isUnmappedRow(field: LitigationPlanFieldMapping): boolean {
  return !field.sourceField;
}

/**
 * 该行可选的台账字段。只提供当前四表模板里的列，不把旧宽表字段放进下拉框。
 */
function mappingOptionsForSheet(
  map: SheetMap | undefined,
  currentField: string,
): Array<{group: string; options: Array<{id: string; label: string}>}> {
  const kind = map?.detectedKind ?? null;
  const pool = litigationFieldsForSheetKind(kind ?? 'cases', LITIGATION_PLAN_STANDARD_FIELDS);
  const byGroup = new Map<string, Array<{id: string; label: string}>>();
  for (const item of pool) {
    const list = byGroup.get(item.group) ?? [];
    list.push({id: item.id, label: item.label});
    byGroup.set(item.group, list);
  }
  const groups = [...byGroup.entries()].map(([group, options]) => ({
    group: GROUP_LABEL[group] ?? group,
    options,
  }));
  // 当前映射不在字段池里时只追加一次。追加到每个分组会让同一个选项重复出现，
  // 下拉框会显示最后一项的「（当前）」而不是字段本名。
  if (currentField && !pool.some((item) => item.id === currentField)) {
    groups.push({
      group: '当前映射',
      options: [{id: currentField, label: `${litigationPlanFieldLabel(currentField)}（当前）`}],
    });
  }
  return groups;
}

/** 行的稳定标识：优先用标准字段 ID，未映射列用列位兜底。 */
function rowKeyOf(field: LitigationPlanFieldMapping, index: number): string {
  return field.sourceField || `col-${field.columnIndex ?? index}`;
}

function groupOf(fieldId: string): string {
  const field = LITIGATION_PLAN_STANDARD_FIELDS.find((item) => item.id === fieldId);
  return field?.group ?? 'meta';
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function formatDateTime(value: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (input: number) => String(input).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** 客户端预览用的示例值：优先取字段填写说明里的 example。 */
function previewSampleValue(fieldId: string): string {
  const guide = litigationFieldFillGuide(fieldId);
  if (guide?.example) return guide.example;
  if (guide?.allowed) return guide.allowed.split('/')[0]!.trim();
  const field = LITIGATION_PLAN_STANDARD_FIELDS.find((item) => item.id === fieldId);
  if (field?.aliases.length) return field.aliases[0]!;
  return '—';
}

/**
 * 挂载平台诉讼台账模板管理器。
 * @param host 页面容器
 * @param canEdit 是否可保存
 * @param onSaved 保存成功后的回调
 */
export async function mountLitigationTemplateManager(
  host: HTMLElement,
  canEdit: boolean,
  onSaved: () => Promise<void>,
): Promise<void> {
  const shell = host.querySelector<HTMLElement>('.litigation-platform-map-shell');
  if (!shell) return;
  shell.innerHTML = '<p class="lt-placeholder">正在加载模板配置…</p>';

  let state: EditorState;
  try {
    const payload = await api.get<{config: PlatformConfig; health: PlatformHealth}>(apiUrl(CONFIG_PATH));
    const config = payload.config;
    const health = payload.health;
    // 按上传的工作簿生成：有几张工作表就有几项，键为真实表名。
    const orderBySheet: Record<string, LitigationPlanFieldMapping[]> = {};
    for (const map of config.sheetMaps) {
      orderBySheet[map.sheetName] = buildOrderForKind(map);
    }
    const first = config.sheetMaps[0];
    state = {
      config,
      health,
      activeSheetName: first?.sheetName ?? '',
      orderBySheet,
      headerRow: first?.headerRow ?? 1,
      dataStartRow: first?.dataStartRow ?? 2,
      dirty: false,
      expandedRuleField: '',
      query: '',
      filter: 'all',
    };
  } catch (error) {
    shell.innerHTML = `<p class="invoice-conflict-note">${escapeHtml(error instanceof Error ? error.message : String(error))}</p>`;
    return;
  }

  const setNotice = (text: string, tone: 'info' | 'error' = 'info') => {
    const el = host.querySelector<HTMLElement>('#litigationManagerNotice');
    if (!el) return;
    el.textContent = text;
    el.hidden = !text;
    el.classList.toggle('is-error', tone === 'error');
  };

  const renderHealth = (health: PlatformHealth): string => {
    if (health.error) {
      return `<div class="lt2-card" data-lt2="health">
      <div class="lt2-card-head">
        <span class="lt2-card-title"><i class="lt2-step">2</i>四张表识别情况</span>
        <span class="status-tag warning">模板文件缺失</span>
      </div>
      <div class="lt2-card-body">
        <p class="lt2-blocker">${escapeHtml(health.error)}</p>
        <p class="lt2-note">服务器上找不到平台模板 Excel。请用右上角「导入新模板」上传一份四表模板，或让运维把模板文件放到下列任一位置：</p>
        <ul class="lt2-paths">${(health.searchedPaths ?? []).map((item) => `<li><code>${escapeHtml(item)}</code></li>`).join('')}</ul>
      </div>
    </div>`;
    }
    const rows = health.sheets.map((item) => {
      // 未识别为四表之一的工作表照样列出，只是标注"未识别类型"
      const kindNote = item.detectedKind ? SHEET_LABEL[item.detectedKind] : '未识别为四表之一';
      // 映射进度：已映射 / 识别到的列数
      const total = Math.max(item.columnCount, item.mappedColumnCount, 1);
      const percent = Math.round((item.mappedColumnCount / total) * 100);
      const counts = item.sheetExists
        ? `<span class="lt2-counts">
             <span class="lt2-pill">已映射 ${item.mappedColumnCount} 列</span>
             ${item.unmappedColumnCount ? `<span class="lt2-pill is-warn">未映射 ${item.unmappedColumnCount} 列</span>` : ''}
             <span class="lt2-meter" role="img" aria-label="映射进度 ${percent}%"><i style="width:${percent}%"></i></span>
           </span>`
        : '<span class="lt2-counts"><span class="lt2-pill is-warn">未找到该工作表</span></span>';
      const detail = `表头第 ${item.headerRow} 行 · 数据第 ${item.dataStartRow} 行`;
      return `<li class="${item.hasIssue ? 'is-warn' : 'is-ok'}">
        <span class="lt2-health-icon" aria-hidden="true">${item.hasIssue ? '!' : '✓'}</span>
        <span class="lt2-health-name">
          <b>${escapeHtml(item.configSheetName)}</b>
          <small>${escapeHtml(kindNote)}</small>
        </span>
        ${counts}
        <span class="lt2-health-meta">${escapeHtml(detail)}</span>
        ${item.issues.length ? `<em class="lt2-health-issue">${escapeHtml(item.issues.join('；'))}</em>` : ''}
      </li>`;
    }).join('');
    const orphan = health.workbookSheetNames.length && !health.ok
      ? `<p class="lt2-health-extra">Excel 中的工作表：${health.workbookSheetNames.map((name) => escapeHtml(name)).join('、')}</p>`
      : '';
    return `<div class="lt2-card" data-lt2="health">
      <div class="lt2-card-head">
        <span class="lt2-card-title"><i class="lt2-step">2</i>四张表识别情况</span>
        <span class="${health.ok ? 'status-tag success' : 'status-tag warning'}">${health.ok ? '全部正常' : '有问题需要处理'}</span>
      </div>
      <div class="lt2-card-body">
        <ul class="lt2-health">${rows}</ul>
        ${orphan}
      </div>
    </div>`;
  };

  const renderTemplateCard = (health: PlatformHealth): string => `<div class="lt2-card" data-lt2="template">
    <div class="lt2-card-head">
      <span class="lt2-card-title"><i class="lt2-step">1</i>当前模板文件</span>
      <span class="${health.hasCustomXlsx ? 'status-tag success' : 'status-tag'}">${health.hasCustomXlsx ? '自定义模板' : '系统内置'}</span>
    </div>
    <div class="lt2-card-body lt2-template-body">
      <div class="lt2-file">
        <strong>${escapeHtml(health.fileName || '诉讼台账模板')}</strong>
        <small>${escapeHtml(formatBytes(health.sizeBytes))} · 更新于 ${escapeHtml(formatDateTime(health.updatedAt))} · 第 ${health.version} 版</small>
      </div>
      <p class="lt2-note">这里配置全站诉讼台账的结构。律师端新建台账时会自动使用这套结构，不需要各自上传模板。</p>
    </div>
  </div>`;

  const renderFieldRow = (field: LitigationPlanFieldMapping, index: number, total: number): string => {
    const fieldId = field.sourceField;
    const unmapped = isUnmappedRow(field);
    const label = unmapped ? '' : litigationPlanFieldLabel(fieldId);
    const enabled = field.enabled !== false;
    const excelHeader = field.header?.trim() || '';
    const rowKey = fieldId || `col-${field.columnIndex}`;
    const expanded = !unmapped && state.expandedRuleField === rowKey;
    const badge = unmapped ? null : ruleBadgeLabel(fieldId, state.config.fieldRuleOverrides);
    const activeMap = state.config.sheetMaps.find((item) => item.sheetName === state.activeSheetName);
    const groups = mappingOptionsForSheet(activeMap, fieldId);
    const selectOptions = [
      '<option value="">— 不映射 —</option>',
      ...groups.map((group) => `<optgroup label="${escapeHtml(group.group)}">${group.options
        .map((item) => `<option value="${escapeHtml(item.id)}" ${item.id === fieldId ? 'selected' : ''}>${escapeHtml(item.label)}</option>`)
        .join('')}</optgroup>`),
    ].join('');
    return `<li class="lt2-col-row${enabled ? '' : ' is-off'}${unmapped ? ' is-unmapped' : ''}${expanded ? ' is-expanded' : ''}" data-field-row="${escapeHtml(rowKey)}" data-column-index="${field.columnIndex}">
      <button type="button" class="lt2-drag" data-field-drag="${escapeHtml(rowKey)}" draggable="${canEdit ? 'true' : 'false'}" title="${canEdit ? '按住拖动调整列顺序' : ''}" aria-label="拖动调整顺序">⠿</button>
      <input type="checkbox" class="lt2-col-toggle" data-field-enabled="${escapeHtml(rowKey)}" ${enabled ? 'checked' : ''} ${canEdit ? '' : 'disabled'} aria-label="启用该列">
      <span class="lt2-col-name">
        ${excelHeader || field.columnLetter
          ? `<input class="lt2-header-input" data-field-header="${field.columnIndex}" value="${escapeHtml(excelHeader || label)}" maxlength="120" aria-label="表头文字" ${canEdit ? '' : 'readonly'}>`
          : `<b>${escapeHtml(label || '（无表头）')}</b>`}
        <small>${escapeHtml(field.columnLetter || '')} 列 · ${unmapped ? '未指定台账字段' : `映射到「${escapeHtml(label)}」`}</small>
      </span>
      <span class="lt2-col-map">
        <select class="lt2-map-select${unmapped ? ' is-unmapped' : ''}" data-field-map="${escapeHtml(String(field.columnIndex))}" ${canEdit ? '' : 'disabled'} aria-label="${escapeHtml(excelHeader || '该列')} 映射到哪个台账字段">${selectOptions}</select>
      </span>
      <span class="lt2-col-source${unmapped ? ' is-warn' : ''}">
        ${unmapped
          ? '<span class="lt2-unmapped-tag">未映射</span>'
          : `<button type="button" class="lt2-rule-btn${badge?.customized ? ' is-custom' : ''}" data-rule-toggle="${escapeHtml(fieldId)}" ${canEdit ? '' : 'disabled'} title="${canEdit ? '点击编辑该字段的填写规则' : ''}">${escapeHtml(badge?.label ?? '')}${badge?.customized ? ' ·已改' : ''}</button>`}
      </span>
      <span class="lt2-col-move">
        <button type="button" class="lt2-move" data-field-move="up" data-field-index="${index}" ${canEdit && index > 0 ? '' : 'disabled'} aria-label="上移">↑</button>
        <button type="button" class="lt2-move" data-field-move="down" data-field-index="${index}" ${canEdit && index < total - 1 ? '' : 'disabled'} aria-label="下移">↓</button>
      </span>
      ${expanded ? `<div class="lt2-rule-host">${renderRuleEditor(fieldId, state.config.fieldRuleOverrides)}</div>` : ''}
    </li>`;
  };

  const visibleFields = (): LitigationPlanFieldMapping[] => {
    const all = state.orderBySheet[state.activeSheetName] ?? [];
    const query = state.query.trim().toLocaleLowerCase();
    return all.filter((field) => {
      const enabled = field.enabled !== false;
      if (state.filter === 'enabled' && !enabled) return false;
      if (state.filter === 'disabled' && enabled) return false;
      if (state.filter === 'unmapped' && !isUnmappedRow(field)) return false;
      if (!query) return true;
      const label = field.sourceField ? litigationPlanFieldLabel(field.sourceField) : '';
      return label.toLocaleLowerCase().includes(query)
        || String(field.sourceField ?? '').toLocaleLowerCase().includes(query)
        || String(field.header ?? '').toLocaleLowerCase().includes(query)
        || String(field.columnLetter ?? '').toLocaleLowerCase() === query;
    });
  };

  const renderFieldList = (): string => {
    const sheet = state.orderBySheet[state.activeSheetName] ?? [];
    const fields = visibleFields();
    const enabledCount = sheet.filter((item) => item.enabled !== false).length;
    const unmappedCount = sheet.filter((item) => isUnmappedRow(item) && item.header?.trim()).length;
    // 当前工作表归属哪类台账字段（未识别时为 null）
    const activeMap = state.config.sheetMaps.find((item) => item.sheetName === state.activeSheetName);
    const activeDetectedKind: LitigationSheetKind | null = activeMap?.detectedKind ?? null;
    // Excel 里真实存在的列按列位顺序平铺（这才是用户表格的样子）；
    // 尚未映射的标准字段目录字段单独归到「可选台账字段」分组。
    const realColumns = fields.filter((item) => item.header?.trim());
    const catalogOnly = fields.filter((item) => !item.header?.trim());
    const groups = new Map<string, LitigationPlanFieldMapping[]>();
    for (const field of catalogOnly) {
      const key = groupOf(field.sourceField);
      const list = groups.get(key) ?? [];
      list.push(field);
      groups.set(key, list);
    }
    const rowOf = (field: LitigationPlanFieldMapping) => {
      const index = sheet.indexOf(field);
      return renderFieldRow(field, index, sheet.length);
    };
    const body = fields.length
      ? `${realColumns.length ? `<div class="lt2-group">
          <p class="lt2-group-title">Excel 里的列<span>${realColumns.length}</span></p>
          <ul class="lt2-cols">${realColumns.map(rowOf).join('')}</ul>
        </div>` : ''}
        ${[...groups.entries()].map(([group, list]) => `<div class="lt2-group">
          <p class="lt2-group-title">可选台账字段 · ${escapeHtml(GROUP_LABEL[group] ?? group)}<span>${list.length}</span></p>
          <ul class="lt2-cols">${list.map(rowOf).join('')}</ul>
        </div>`).join('')}`
      : '<p class="lt-placeholder">没有符合条件的字段。</p>';
    return `<div class="lt2-card" data-lt2="fields">
      <div class="lt2-card-head">
        <span class="lt2-card-title"><i class="lt2-step">3</i>字段与列</span>
        <div class="lt2-head-tools">
          <label class="lt2-search"><input type="search" id="litigationFieldSearch" placeholder="搜索表头 / 台账字段 / 列位" value="${escapeHtml(state.query)}"></label>
          <select class="lt2-filter" id="litigationFieldFilter" aria-label="筛选字段">
            <option value="all" ${state.filter === 'all' ? 'selected' : ''}>全部</option>
            <option value="enabled" ${state.filter === 'enabled' ? 'selected' : ''}>仅已启用</option>
            <option value="disabled" ${state.filter === 'disabled' ? 'selected' : ''}>仅未启用</option>
            <option value="unmapped" ${state.filter === 'unmapped' ? 'selected' : ''}>仅未映射</option>
          </select>
        </div>
      </div>
      <div class="lt2-sheet-tabs" role="tablist" aria-label="工作表">
        ${state.config.sheetMaps.map((map) => {
          const list = state.orderBySheet[map.sheetName] ?? [];
          const on = list.filter((item) => item.enabled !== false && item.header?.trim()).length;
          const cols = list.filter((item) => item.header?.trim()).length;
          const health = state.health.sheets.find((item) => item.configSheetName === map.sheetName);
          const isActive = map.sheetName === state.activeSheetName;
          return `<button type="button" class="lt2-sheet-tab${isActive ? ' is-active' : ''}" data-sheet-name="${escapeHtml(map.sheetName)}" role="tab" aria-selected="${isActive ? 'true' : 'false'}">
            ${escapeHtml(map.sheetName)}
            <small>${cols} 列 · 已映射 ${on}${health?.hasIssue ? ' · ⚠' : ''}</small>
          </button>`;
        }).join('')}
      </div>
      <div class="lt2-sheet-kind-bar">
        <label class="lt2-inline-field">这张表属于
          <select class="lt2-filter lt2-kind-select" id="litigationSheetKind" ${canEdit ? '' : 'disabled'} aria-label="这张表属于哪类台账字段">
            <option value="" ${!activeDetectedKind ? 'selected' : ''}>不参与台账（仅保留结构）</option>
            ${LITIGATION_DEFAULT_SHEETS.map((spec) => `<option value="${spec.kind}" ${activeDetectedKind === spec.kind ? 'selected' : ''}>${escapeHtml(SHEET_LABEL[spec.kind])}</option>`).join('')}
          </select>
        </label>
        <span class="lt2-row-hint">${activeDetectedKind
          ? '切换后会自动按新的字段类别重新识别列映射，仍可手工调整。'
          : '当前工作表未参与台账；选择类别后即可把它的列映射到台账字段。'}</span>
      </div>
      <div class="lt2-card-body">
        <div class="lt2-row-config">
          <label class="lt2-inline-field">表头行<input type="number" min="1" max="9999" id="litigationHeaderRow" value="${state.headerRow}" ${canEdit ? '' : 'readonly'}></label>
          <label class="lt2-inline-field">数据起始行<input type="number" min="2" max="10000" id="litigationDataStartRow" value="${state.dataStartRow}" ${canEdit ? '' : 'readonly'}></label>
          <span class="lt2-row-hint">Excel 共 ${realColumns.length || sheet.filter((i) => i.header?.trim()).length} 列，已映射 ${enabledCount} 列${unmappedCount ? `，${unmappedCount} 列未映射` : ''}</span>
        </div>
        ${body}
      </div>
      <div class="lt2-save-bar">
        <span class="lt2-dirty">${state.dirty ? '有未保存的修改' : '已与线上一致'}</span>
        <span class="lt2-save-actions">
          <button type="button" class="secondary-button" id="litigationManagerDiscard" ${state.dirty && canEdit ? '' : 'disabled'}>放弃修改</button>
          <button type="button" class="primary-button" id="litigationManagerSave" ${state.dirty && canEdit ? '' : 'disabled'}>保存并发布</button>
        </span>
      </div>
    </div>`;
  };

  const renderPreview = (): string => {
    const sheet = (state.orderBySheet[state.activeSheetName] ?? []).filter((item) => item.enabled !== false);
    if (!sheet.length) {
      return `<div class="lt2-card" data-lt2="preview">
        <div class="lt2-card-head"><span class="lt2-card-title"><i class="lt2-step">4</i>客户端预览</span></div>
        <div class="lt2-card-body"><p class="lt-placeholder">当前工作表没有启用任何列。</p></div>
      </div>`;
    }
    const head = sheet.map((field) => `<th>${escapeHtml(field.header?.trim() || litigationPlanFieldLabel(field.sourceField))}</th>`).join('');
    const sample = sheet.map((field) => `<td>${escapeHtml(previewSampleValue(field.sourceField))}</td>`).join('');
    const mapped = sheet.filter((item) => item.sourceField).length;
    const unmapped = sheet.length - mapped;
    return `<div class="lt2-card" data-lt2="preview">
      <div class="lt2-card-head">
        <span class="lt2-card-title"><i class="lt2-step">4</i>客户端预览</span>
        <span class="lt2-muted-inline">${escapeHtml(state.activeSheetName || '工作表')} · 与你上传的 Excel 完全一致：${sheet.length} 列</span>
      </div>
      <div class="lt2-card-body">
        <div class="lt2-preview-scroll"><table class="lt2-preview-table"><thead><tr>${head}</tr></thead><tbody><tr>${sample}</tr></tbody></table></div>
        <p class="lt2-note">这里的表头与列序就是你 Excel 里的样子（未做任何替换）。${unmapped ? `其中 ${unmapped} 列尚未映射到台账字段，导出时不会写入数据。` : ''}示例值仅用于占位，不代表真实案件数据。</p>
      </div>
    </div>`;
  };

  const render = () => {
    // 模板文件读不到时，字段配置没有依据（列位无从校验），只渲染模板卡与阻断提示。
    const blocked = Boolean(state.health.error);
    shell.innerHTML = blocked
      ? `${renderTemplateCard(state.health)}${renderHealth(state.health)}`
      : `${renderTemplateCard(state.health)}${renderHealth(state.health)}${renderFieldList()}${renderPreview()}`;
    if (!blocked) bind();
  };

  const syncRowConfigFromInputs = () => {
    const headerInput = shell.querySelector<HTMLInputElement>('#litigationHeaderRow');
    const dataInput = shell.querySelector<HTMLInputElement>('#litigationDataStartRow');
    if (!headerInput || !dataInput) return;
    const headerRow = Math.max(1, Number(headerInput.value) || 1);
    const dataStartRow = Math.max(2, Number(dataInput.value) || 2);
    if (headerRow !== state.headerRow || dataStartRow !== state.dataStartRow) {
      state.headerRow = headerRow;
      state.dataStartRow = dataStartRow;
      state.dirty = true;
    }
  };

  const markDirty = () => {
    state.dirty = true;
    const dirtyLabel = shell.querySelector<HTMLElement>('.lt2-dirty');
    if (dirtyLabel) dirtyLabel.textContent = '有未保存的修改';
    const save = shell.querySelector<HTMLButtonElement>('#litigationManagerSave');
    if (save) save.disabled = !canEdit;
    const discard = shell.querySelector<HTMLButtonElement>('#litigationManagerDiscard');
    if (discard) discard.disabled = !canEdit;
  };

  /** 调整某一行的位置。Excel 列位不变，保存后的行序就是台账里的列序。 */
  const moveFieldByIndex = (index: number, direction: -1 | 1) => {
    const list = state.orderBySheet[state.activeSheetName] ?? [];
    const target = index + direction;
    if (index < 0 || target < 0 || target >= list.length) return;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    state.orderBySheet[state.activeSheetName] = next;
    state.dirty = true;
    render();
  };

  /** 勾选/取消勾选某一行的启用状态（按行下标定位，兼容未映射列）。 */
  const toggleRowEnabled = (rowKey: string, enabled: boolean) => {
    const list = state.orderBySheet[state.activeSheetName] ?? [];
    const next = list.map((item, index) => (rowKeyOf(item, index) === rowKey ? {...item, enabled} : item));
    state.orderBySheet[state.activeSheetName] = next;
    state.dirty = true;
    render();
  };

  /**
   * 修改这一列在台账里显示的表头文字。列位和字段映射不变。
   * @param columnIndex 列序号
   * @param header 新表头
   */
  const renameColumnHeader = (columnIndex: number, header: string) => {
    const list = state.orderBySheet[state.activeSheetName] ?? [];
    state.orderBySheet[state.activeSheetName] = list.map((item) => (
      item.columnIndex === columnIndex ? {...item, header: header.slice(0, 120)} : item
    ));
    state.dirty = true;
    const dirtyLabel = shell.querySelector<HTMLElement>('.lt2-dirty');
    if (dirtyLabel) dirtyLabel.textContent = '有未保存的修改';
    const save = shell.querySelector<HTMLButtonElement>('#litigationManagerSave');
    const discard = shell.querySelector<HTMLButtonElement>('#litigationManagerDiscard');
    if (save) save.disabled = !canEdit;
    if (discard) discard.disabled = !canEdit;
  };

  /** 手工指定某一列映射到哪个台账字段（列位与表头不变）。 */
  const setColumnMapping = (columnIndex: number, nextField: string) => {
    const list = state.orderBySheet[state.activeSheetName] ?? [];
    const next = list.map((item) => {
      if (item.columnIndex !== columnIndex) {
        // 同一台账字段不能映射到多列：清除其他列的相同映射
        if (nextField && item.sourceField === nextField) return {...item, sourceField: '', enabled: false};
        return item;
      }
      return {...item, sourceField: nextField, enabled: Boolean(nextField)};
    });
    state.orderBySheet[state.activeSheetName] = next;
    state.dirty = true;
    render();
  };

  let dragRowKey = '';

  /**
   * 更新某字段的填写规则覆盖。传空对象并 reset=true 表示恢复系统默认。
   */
  const setRuleOverride = (
    fieldId: string,
    patch: Partial<NonNullable<LitigationFieldRuleOverrides[string]>>,
    reset = false,
  ) => {
    const current = {...(state.config.fieldRuleOverrides ?? {})};
    if (reset) {
      delete current[fieldId];
    } else {
      current[fieldId] = {...(current[fieldId] ?? {}), ...patch};
    }
    state.config = {...state.config, fieldRuleOverrides: current};
    state.dirty = true;
    render();
  };

  const bind = () => {
    shell.querySelectorAll<HTMLButtonElement>('[data-sheet-name]').forEach((button) => {
      button.addEventListener('click', () => {
        syncRowConfigFromInputs();
        const name = button.dataset.sheetName || '';
        if (!name || name === state.activeSheetName) return;
        state.activeSheetName = name;
        state.expandedRuleField = '';
        const map = state.config.sheetMaps.find((item) => item.sheetName === name);
        state.headerRow = map?.headerRow ?? 1;
        state.dataStartRow = map?.dataStartRow ?? 2;
        render();
      });
    });

    const search = shell.querySelector<HTMLInputElement>('#litigationFieldSearch');
    search?.addEventListener('input', () => {
      state.query = search.value;
      const active = document.activeElement === search;
      render();
      if (active) {
        const next = shell.querySelector<HTMLInputElement>('#litigationFieldSearch');
        next?.focus();
        next?.setSelectionRange(next.value.length, next.value.length);
      }
    });
    const filter = shell.querySelector<HTMLSelectElement>('#litigationFieldFilter');
    filter?.addEventListener('change', () => {
      state.filter = (filter.value || 'all') as EditorState['filter'];
      render();
    });

    // 「这张表属于」——手动指定工作表对应的台账字段类别
    const kindSelect = shell.querySelector<HTMLSelectElement>('#litigationSheetKind');
    kindSelect?.addEventListener('change', () => {
      if (!canEdit) return;
      const raw = kindSelect.value;
      const nextKind = (raw || null) as LitigationSheetKind | null;
      const map = state.config.sheetMaps.find((item) => item.sheetName === state.activeSheetName);
      if (!map) return;
      // 按新类别重新识别每一列，再交给 buildOrderForKind 补齐可选台账字段
      const remapped = remapRowsForKind(state.orderBySheet[state.activeSheetName] ?? [], nextKind);
      const mergedMap: SheetMap = {
        ...map,
        detectedKind: nextKind,
        sheetKind: nextKind ?? map.sheetKind,
        exportFields: remapped.filter((item) => item.header?.trim()),
      };
      state.orderBySheet[state.activeSheetName] = buildOrderForKind(mergedMap);
      state.config = {
        ...state.config,
        sheetMaps: state.config.sheetMaps.map((item) => (item.sheetName === state.activeSheetName ? mergedMap : item)),
      };
      state.dirty = true;
      render();
    });

    shell.querySelectorAll<HTMLInputElement>('[data-field-enabled]').forEach((input) => {
      input.addEventListener('change', () => {
        toggleRowEnabled(input.dataset.fieldEnabled || '', input.checked);
      });
    });

    shell.querySelectorAll<HTMLButtonElement>('[data-field-move]').forEach((button) => {
      button.addEventListener('click', () => {
        moveFieldByIndex(Number(button.dataset.fieldIndex) || 0, button.dataset.fieldMove === 'up' ? -1 : 1);
      });
    });

    // ── 手工指定列映射 ────────────────────────────────────────
    shell.querySelectorAll<HTMLInputElement>('[data-field-header]').forEach((input) => {
      input.addEventListener('input', () => {
        if (!canEdit || input.readOnly) return;
        renameColumnHeader(Number(input.dataset.fieldHeader) || 0, input.value);
      });
    });

    shell.querySelectorAll<HTMLSelectElement>('[data-field-map]').forEach((select) => {
      select.addEventListener('change', () => {
        if (!canEdit) return;
        setColumnMapping(Number(select.dataset.fieldMap) || 0, select.value);
      });
    });

    // ── 字段填写规则编辑 ──────────────────────────────────────
    shell.querySelectorAll<HTMLButtonElement>('[data-rule-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!canEdit) return;
        const rowKey = button.dataset.ruleToggle || '';
        state.expandedRuleField = state.expandedRuleField === rowKey ? '' : rowKey;
        render();
      });
    });
    shell.querySelectorAll<HTMLSelectElement>('[data-rule-kind]').forEach((select) => {
      select.addEventListener('change', () => {
        setRuleOverride(select.dataset.ruleKind || '', {ruleKind: select.value as never});
      });
    });
    shell.querySelectorAll<HTMLTextAreaElement>('[data-rule-text]').forEach((area) => {
      area.addEventListener('change', () => {
        const fieldId = area.dataset.ruleText || '';
        const value = area.value.trim();
        if (value) setRuleOverride(fieldId, {rule: value});
        else setRuleOverride(fieldId, {rule: ''}, true);
      });
    });
    shell.querySelectorAll<HTMLInputElement>('[data-rule-allowed]').forEach((input) => {
      input.addEventListener('change', () => {
        setRuleOverride(input.dataset.ruleAllowed || '', {allowed: input.value.trim()});
      });
    });
    shell.querySelectorAll<HTMLInputElement>('[data-rule-example]').forEach((input) => {
      input.addEventListener('change', () => {
        setRuleOverride(input.dataset.ruleExample || '', {example: input.value.trim()});
      });
    });
    shell.querySelectorAll<HTMLInputElement>('[data-rule-court-layer]').forEach((input) => {
      input.addEventListener('change', () => {
        const fieldId = input.dataset.ruleCourtField || '';
        const layer = input.dataset.ruleCourtLayer || '';
        if (!fieldId || !layer) return;
        const current = state.config.fieldRuleOverrides?.[fieldId]?.courtNumberExamples ?? {};
        setRuleOverride(fieldId, {courtNumberExamples: {...current, [layer]: input.value.trim()}});
      });
    });
    shell.querySelectorAll<HTMLButtonElement>('[data-rule-reset]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        setRuleOverride(button.dataset.ruleReset || '', {}, true);
      });
    });

    shell.querySelectorAll<HTMLElement>('[data-field-drag]').forEach((handle) => {
      if (!canEdit) return;
      const row = handle.closest<HTMLElement>('[data-field-row]');
      handle.addEventListener('dragstart', (event) => {
        dragRowKey = row?.dataset.fieldRow || '';
        row?.classList.add('is-dragging');
        event.dataTransfer?.setData('text/plain', dragRowKey);
      });
      handle.addEventListener('dragend', () => {
        dragRowKey = '';
        row?.classList.remove('is-dragging');
        shell.querySelectorAll('.is-drop-target').forEach((el) => el.classList.remove('is-drop-target'));
      });
    });

    shell.querySelectorAll<HTMLElement>('[data-field-row]').forEach((row) => {
      if (!canEdit) return;
      row.addEventListener('dragover', (event) => {
        if (!dragRowKey || dragRowKey === row.dataset.fieldRow) return;
        event.preventDefault();
        row.classList.add('is-drop-target');
      });
      row.addEventListener('dragleave', () => row.classList.remove('is-drop-target'));
      row.addEventListener('drop', (event) => {
        event.preventDefault();
        row.classList.remove('is-drop-target');
        const targetKey = row.dataset.fieldRow || '';
        if (!dragRowKey || !targetKey || dragRowKey === targetKey) return;
        const list = [...(state.orderBySheet[state.activeSheetName] ?? [])];
        const from = list.findIndex((item, index) => rowKeyOf(item, index) === dragRowKey);
        const to = list.findIndex((item, index) => rowKeyOf(item, index) === targetKey);
        if (from < 0 || to < 0) return;
        const [moved] = list.splice(from, 1);
        list.splice(to, 0, moved);
        // 只调整行序。Excel 列字母不变，保存后按这个行序展示。
        state.orderBySheet[state.activeSheetName] = list;
        state.dirty = true;
        render();
      });
    });

    shell.querySelector('#litigationHeaderRow')?.addEventListener('change', () => {
      syncRowConfigFromInputs();
      render();
    });
    shell.querySelector('#litigationDataStartRow')?.addEventListener('change', () => {
      syncRowConfigFromInputs();
      render();
    });

    shell.querySelector('#litigationManagerDiscard')?.addEventListener('click', () => {
      void mountLitigationTemplateManager(host, canEdit, onSaved);
    });

    shell.querySelector('#litigationManagerSave')?.addEventListener('click', () => {
      void (async () => {
        syncRowConfigFromInputs();
        if (state.dataStartRow <= state.headerRow) {
          setNotice('数据起始行必须大于表头行', 'error');
          return;
        }
        const button = shell.querySelector<HTMLButtonElement>('#litigationManagerSave');
        if (button) {
          button.disabled = true;
          button.textContent = '正在保存…';
        }
        try {
          // 严格按上传的工作簿重建：有几张表提交几张，未识别类型的表也照原样保留。
          const sheetMaps: SheetMap[] = state.config.sheetMaps.map((prior) => {
            const isActive = prior.sheetName === state.activeSheetName;
            return {
              sheetKind: prior.sheetKind,
              detectedKind: prior.detectedKind ?? null,
              sheetName: prior.sheetName,
              headerRow: isActive ? state.headerRow : prior.headerRow,
              dataStartRow: isActive ? state.dataStartRow : prior.dataStartRow,
              exportFields: state.orderBySheet[prior.sheetName] ?? prior.exportFields,
            };
          });
          const casesMap = sheetMaps.find((item) => item.detectedKind === 'cases') ?? sheetMaps[0];
          const payload = {
            ...state.config,
            sheetMaps,
            sheetNames: state.config.sheetNames ?? sheetMaps.map((item) => item.sheetName),
            fieldRuleOverrides: state.config.fieldRuleOverrides ?? {},
            exportFields: casesMap?.exportFields ?? state.config.exportFields,
            headerRow: casesMap?.headerRow ?? state.config.headerRow,
            dataStartRow: casesMap?.dataStartRow ?? state.config.dataStartRow,
            sheetName: casesMap?.sheetName ?? state.config.sheetName,
            listFieldIds: (casesMap?.exportFields ?? [])
              .filter((item) => item.enabled !== false && item.sourceField)
              .map((item) => item.sourceField),
          };
          await api.put(apiUrl(CONFIG_PATH), payload);
          if (currentAdminEnvironment() === 'production') {
            await runAgainstTestAdmin(() => api.put(apiUrl(CONFIG_PATH), payload));
          }
          setNotice('已保存并发布。客户端测试站下次打开台账时会使用这个字段顺序。');
          await onSaved();
          await mountLitigationTemplateManager(host, canEdit, onSaved);
        } catch (error) {
          setNotice(error instanceof Error ? error.message : String(error), 'error');
          if (button) {
            button.disabled = false;
            button.textContent = '保存并发布';
          }
        }
      })();
    });
  };

  render();
}
